// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewChild, ViewChildren, QueryList, ElementRef, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray, FormControl, NgModel } from '@angular/forms';
import { ButtonsComponent } from "../../../shared/buttons/buttons.component";
import { ModalDialogComponent } from '../../../shared/modal-dialog/modal-dialog.component';
import { IconsComponent } from '../../../shared/icons/icons.component';
import { RangeValidatorDirective } from '../../../shared/hce/directives/range-validator.directive';
import { BothEmptyValidator } from '../../../shared/hce/directives/both-empty-validator.directive';
import { BothEmptyErrorStateMatcher } from '../../../shared/hce/directives/both-empty-error-state-matcher';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';
import { HceSvFilterService } from '../../../services/hce/hce-sv-filter/hce-sv-filter.service'
import {
  HceSvFilter,
  SvFilterPhysicalPosition,
  SvFilterSvLength,
  SvFilterMatchAnnotation,
} from '../../../interfaces/hce/hce-sv-filter';
import {
  // constant
  SCREEN_TYPE_SV,
  DEFAULT_POSITION,
  DEFAULT_POSITION_MIN,
  DEFAULT_POSITION_MAX,
  POSITION_MIN,
  POSITION_MAX,
  DEFAULT_SV_LENGTH,
  DEFAULT_THRESHOLD,
  INTERVAL_DEPTH_MIN,
  INTERVAL_DEPTH_MAX,
  QUALITY_SCORE_MIN,
  QUALITY_SCORE_MAX,
  PRESENCE_MIN,
  PRESENCE_MAX,
  GENE_NAME_MAX_LENGTH,
  // SV Type
  SvFilterSvType,
  SvFilterSvTypeKey,
  // Zygosity
  SvFilterZygosity,
  SvFilterZygosityKey,
  // Flagged SV
  SvFilterFlaggedSv,
  SvFilterFlaggedSvKey,
  // Overlap KG
  SvFilterOverlapKG,
  SvFilterOverlapKGKey,
  // SV Reason
  SvFilterSVReason,
  SvFilterSVReasonKey,
  // Mol Check
  SvFilterMolCheckItem,
  SvFilterMolCheckKey,
  // Masked
  SvFilterMasked,
  SvFilterMaskedKey,
  // Best SV per Cluster
  SvFilterBestSVperCluster,
  SvFilterBestSVperClusterKey,
} from '../../../shared/constants/hce/hce-sv-filter-constants';

// SV Filter Modal Open Parameter
export type HceSvFilterModalData = {
  svFilter: HceSvFilter;
  annotationFiles: string[];
  refNameMap: Record<string, string>;
}

// side menu item
type SectionKey = Exclude<keyof HceSvFilter, 'filterName' | 'screenType'> | 'saves';

interface SectionItem {
  key: SectionKey;
  icon: string;
  label: string;
}

// For save list display
type SaveItem = {
  key: string; // Full key
  label: string; // Key string without the leading savePrefix
}

// summary cards grid Item
type CardItem = {
  title: string;
  items: CardChip[];
};

type CardChip = {
  sub?: string; // Truncate if it does not fit
  main: string; // Show all
};

type NameValue = { name: string; value: string | number };

// Data for a single configurable item
type PhysicalPositionForm = {
  chr?: FormControl<string | null>;
  posStart?: FormControl<number | null>;
  posEnd: FormControl<number | null>;
  posBp?: FormControl<number | null>;
};

type SvLengthForm = {
  lenMin?: FormControl<number | null>;
  lenMinBp?: FormControl<number | null>;
  lenMax?: FormControl<number | null>;
  lenMaxBp?: FormControl<number | null>;
};

type MatchAnnotationForm = {
  annotation?: FormControl<string | null>;
  threshold?: FormControl<number | null>;
  thresholdunit?: FormControl<string | null>;
  excludeMatches?: FormControl<boolean | null>;
};

/**
 * SV Filter modal
 */
@Component({
  selector: 'app-sv-filter-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IconsComponent,
    RangeValidatorDirective,

    // Material
    MatBadgeModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDividerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatOptionModule,
    MatSelectModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatTooltipModule,
    ButtonsComponent,
  ],
  templateUrl: './hce-sv-filter-modal.component.html',
  styleUrls: ['./hce-sv-filter-modal.component.css', '../../../shared/buttons/buttons.component.css']
})
export class HceSvFilterModalComponent implements OnInit {
  // Defined as public for access from the template
  public readonly SvFilterSvType = SvFilterSvType;
  public readonly SvFilterZygosity = SvFilterZygosity;
  public readonly SvFilterFlaggedSv = SvFilterFlaggedSv;
  public readonly SvFilterOverlapKG = SvFilterOverlapKG;
  public readonly SvFilterMolCheck = SvFilterMolCheckItem;
  public readonly SvFilterMasked = SvFilterMasked;
  public readonly SvFilterSVReason = SvFilterSVReason;
  public readonly SvFilterBestSVperCluster = SvFilterBestSVperCluster;
  public readonly POSITION_MIN = POSITION_MIN;
  public readonly POSITION_MAX = POSITION_MAX;
  public readonly INTERVAL_DEPTH_MIN = INTERVAL_DEPTH_MIN;
  public readonly INTERVAL_DEPTH_MAX = INTERVAL_DEPTH_MAX;
  public readonly QUALITY_SCORE_MIN = QUALITY_SCORE_MIN;
  public readonly QUALITY_SCORE_MAX = QUALITY_SCORE_MAX;
  public readonly PRESENCE_MIN = PRESENCE_MIN;
  public readonly PRESENCE_MAX = PRESENCE_MAX;
  public readonly GENE_NAME_MAX_LENGTH = GENE_NAME_MAX_LENGTH;

  private readonly fb = inject(FormBuilder);
  private readonly svFilterSvc = inject(HceSvFilterService);

  // Parameters linked with the caller
  annotations: string[] = []; // Selected annotations
  svFilter: HceSvFilter = {}; // SV Filter
  chrs: Array<{ value: string; label: string }> = []; // Chromosome

  // SV filter save data
  userId: string = String(this.getUserId() ?? '');
  saveName = '';
  savePrefix = 'hce_svfilter_' + this.userId + '_';
  saveList: SaveItem[] = [];

   // Simple section registry
  sections: SectionItem[] = [
    { key: 'physicalPosition', icon: 'location_on', label: 'Physical Position' },
    { key: 'physicalPositionHide', icon: 'visibility_off', label: 'Physical Position (Hide)' },
    { key: 'svLength', icon: 'straighten', label: 'SV Length' },
    { key: 'svType', icon: 'category', label: 'SV Type' },
    { key: 'zygosity', icon: 'splitscreen', label: 'Zygosity' },
    { key: 'flaggedSV', icon: 'flag', label: 'Flagged SV' },
    { key: 'geneName', icon: 'article', label: 'Gene Name' },
    { key: 'svsWithRefGenesDetected', icon: 'verified', label: 'SVs with Ref Genes detected' },
    { key: 'matchAnnotation', icon: 'link', label: 'Match to Annotation' },
    { key: 'minIntervalDepth', icon: 'water', label: 'Minimum Interval Depth' },
    { key: 'medianIntervalDepth', icon: 'water_drop', label: 'Median Interval Depth' },
    { key: 'qualityScore', icon: 'speed', label: 'Quality Score' },
    { key: 'svReason', icon: 'help_center', label: 'SV Reason' },
    { key: 'svBreakpointMoleculeCheck', icon: 'rule', label: 'SV Breakpoint Molecule Check' },
    { key: 'svsBelowPresenceControlDb', icon: 'percent', label: 'SVs Below Presence in Control DB (%)' },
    { key: 'masked', icon: 'mask', label: 'Masked' },
    { key: 'bestSVperCluster', icon: 'mask', label: 'Best SV per Cluster' },
  ];

  activeSection: SectionKey | null = null;

  // Unit selection list
  unitsKM: Array<{ value: number; label: string }> = [
    {value: 1, label: 'bp'},
    {value: 1000, label: 'Kbp'},
    {value: 1000000, label: 'Mbp'},
  ];
  unitsKMG: Array<{ value: number; label: string }> = [
    {value: 1, label: 'bp'},
    {value: 1000, label: 'Kbp'},
    {value: 1000000, label: 'Mbp'},
    {value: 1000000000, label: 'Gbp'},
  ];
  unitsPercent: Array<{ value: string; label: string }> = [
    {value: 'bp', label: 'bp'},
    {value: 'per', label: '%'},
  ];

  // Drawer control
  private readonly snackBar = inject(MatSnackBar);
  drawerOpened = false;
  drawerClosed = true; // Due to animation lag, keep a separate flag to indicate when closing is complete

  /*
   * SV Filter Paramator
   */
  bothEmptyMatcher = new BothEmptyErrorStateMatcher();

  // Physical Position
  formPositions = this.fb.group({
    items: this.fb.array<FormGroup<PhysicalPositionForm>>([])
  });
  get posItems(): FormArray<FormGroup<PhysicalPositionForm>> {
    return this.formPositions.controls.items as FormArray<FormGroup<PhysicalPositionForm>>;
  }

  // Physical Position(Hide)
  formPositionsHide = this.fb.group({
    items: this.fb.array<FormGroup<PhysicalPositionForm>>([])
  });
  get posHideItems(): FormArray<FormGroup<PhysicalPositionForm>> {
    return this.formPositionsHide.controls.items as FormArray<FormGroup<PhysicalPositionForm>>;
  }

  // SV Length
  formSvLength = this.fb.group({
    items: this.fb.array<FormGroup<SvLengthForm>>([])
  });
  get svLengthItems(): FormArray<FormGroup<SvLengthForm>> {
    return this.formSvLength.controls.items as FormArray<FormGroup<SvLengthForm>>;
  }

  // SV Type
  public readonly svTypeKeys = this.getKeys(SvFilterSvType); // Array of key names
  selectSvTypeByKey: Record<SvFilterSvTypeKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterSvType);

  // Zygosity
  public readonly zygosityKeys = this.getKeys(SvFilterZygosity);
  selectZygosityByKey: Record<SvFilterZygosityKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterZygosity);

  // Flagged SV
  public readonly flaggedSvKeys = this.getKeys(SvFilterFlaggedSv);
  selectFlaggedSvByKey: Record<SvFilterFlaggedSvKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterFlaggedSv);

  // Gene Name
  geneNameForm!: FormGroup;
  geneName = new FormControl('', [Validators.maxLength(GENE_NAME_MAX_LENGTH)]);

  // SVs with Ref Genes detected (OverlapKG)
  public readonly overlapKGKeys = this.getKeys(SvFilterOverlapKG);
  selectOverlapKGByKey: Record<SvFilterOverlapKGKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterOverlapKG);

  // Match to Annotation
  formMatchAnnotation = this.fb.group({
    items: this.fb.array<FormGroup<MatchAnnotationForm>>([])
  });
  get matchAnnotationItems(): FormArray<FormGroup<MatchAnnotationForm>> {
    return this.formMatchAnnotation.controls.items as FormArray<FormGroup<MatchAnnotationForm>>;
  }

  // Minimum Interval Depth
  minIntervalDepthMin: string = '';
  minIntervalDepthMax: string = '';
  @ViewChild('minIntervalDepthMinModel') minIntervalDepthMinCtrl!: NgModel;
  @ViewChild('minIntervalDepthMaxModel') minIntervalDepthMaxCtrl!: NgModel;

  // Median Interval Depth
  medianIntervalDepthMin: string = '';
  medianIntervalDepthMax: string = '';
  @ViewChild('medianIntervalDepthMinModel') medianIntervalDepthMinCtrl!: NgModel;
  @ViewChild('medianIntervalDepthMaxModel') medianIntervalDepthMaxCtrl!: NgModel;

  // Quality Score
  qualityMin: string = '';
  qualityMax: string = '';
  @ViewChild('qualityMinModel') qualityMinCtrl!: NgModel;
  @ViewChild('qualityMaxModel') qualityMaxCtrl!: NgModel;

  // SV Reason
  public readonly svReasonKeys = this.getKeys(SvFilterSVReason);
  selectSVReasonByKey: Record<SvFilterSVReasonKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterSVReason);

  // SV Breakpoint Molecule Check (MolCheck)
  public readonly molCheckKeys = this.getKeys(SvFilterMolCheckItem);
  selectMolCheckByKey: Record<SvFilterMolCheckKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterMolCheckItem);

  // SVs Below Presence in Control DB (%)
  svsBelowPresenceCoLoRSdBControlDb: string = '';
  svsBelowPresenceGnomADControlDb: string = '';
  svsBelowPresenceOntLRSControlDb: string = '';
  @ViewChild('svsBelowPresenceCoLoRSdBControlDbModel') svsBelowPresenceCoLoRSdBControlDbCtrl!: NgModel;
  @ViewChild('svsBelowPresenceGnomADControlDbModel') svsBelowPresenceGnomADControlDbCtrl!: NgModel;
  @ViewChild('svsBelowPresenceOntLRSControlDbModel') svsBelowPresenceOntLRSControlDbCtrl!: NgModel;

  // Masked
  public readonly maskedKeys = this.getKeys(SvFilterMasked);
  selectMaskedByKey: Record<SvFilterMaskedKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterMasked);

  // Best SV per Cluster
  public readonly bestSVperClusterKeys = this.getKeys(SvFilterBestSVperCluster);
  selectBestSVperClusterByKey: Record<SvFilterBestSVperClusterKey, boolean> =
    this.buildInitialSelectedByKey(SvFilterBestSVperCluster);

  constructor(
    public dialogRef: MatDialogRef<HceSvFilterModalComponent, { filter: HceSvFilter; count: number } | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: HceSvFilterModalData,
    private messageModal: MessageModalService,
    private dialog: MatDialog,
  ) {
    this.geneNameForm = this.fb.group({
      geneName: this.geneName,
    });
  }

  /*
   * summary cards grid
   */

  readonly MAX_DISPLAY = 5; // Display limit count

  // For card chip adjustment
  @ViewChildren('chipEl', { read: ElementRef }) chipElements!: QueryList<ElementRef>;

  // Card display data
  cardItems: Record<SectionKey, CardItem> = {
    physicalPosition: { title: 'Physical Position', items: [] },
    physicalPositionHide: { title: 'Physical Position(Hide)', items: [] },
    svType: { title: 'SV Type', items: [] },
    svLength: { title: 'SV Length', items: [] },
    zygosity: { title: 'Zygosity', items: [] },
    flaggedSV: { title: 'Flagged SV', items: [] },
    geneName: { title: 'Gene Name', items: [] },
    svsWithRefGenesDetected: { title: 'SVs with Ref Genes detected', items: [] },
    matchAnnotation: { title: 'Match to Annotation', items: [] },
    minIntervalDepth: { title: 'Minimum Interval Depth', items: [] },
    medianIntervalDepth: { title: 'Median Interval Depth', items: [] },
    qualityScore: { title: 'Quality Score', items: [] },
    svReason: { title: 'SV Reason', items: [] },
    svBreakpointMoleculeCheck: { title: 'SV Breakpoint Molecule Check', items: [] },
    svsBelowPresenceControlDb: { title: 'SVs Below Presence in Control DB (%)', items: [] },
    masked: { title: 'Masked', items: [] },
    bestSVperCluster: { title: 'Best SV per Cluster', items: [] },
    saves: { title: '', items: [] }, // Not applicable to cards
  };

  cardKeys: Array<keyof typeof this.cardItems> = [
    'physicalPosition', 'physicalPositionHide', 'svLength',
    'svType', 'zygosity', 'flaggedSV',
    'geneName', 'svsWithRefGenesDetected', 'matchAnnotation', 'minIntervalDepth', 'medianIntervalDepth',
    'qualityScore', 'svReason', 'svBreakpointMoleculeCheck', 'svsBelowPresenceControlDb', 'masked', 'bestSVperCluster',
  ];

  // Record that stores the expanded state for each key
  cardExpanded: Record<string, boolean> = {};

  ngOnInit(): void {
    this.svFilter = structuredClone(this.data.svFilter);
    this.annotations = structuredClone(this.data.annotationFiles);

    // Format for list display
    const chrPrefix = 'chr';
    this.chrs = Object.entries(this.data.refNameMap).map(([key, value]) => ({
      value: key,
      label: value.indexOf(chrPrefix) >= 0 ? value.substring(chrPrefix.length) : value,
    }));

    // Exclude items that cannot be configured
    this.removeDisallowedItems(this.svFilter);

    this.saveList = this.getSaveList();
  }

  ngAfterViewInit(): void {
    // Execute after the completion of Angular's Change Detection cycle (synchronous processing)
    Promise.resolve().then(() => {
      this.applyFilterToCardItem(this.svFilter);

      // Wait until chipElements are ready
      this.waitForChipElements();
      // Change monitoring
      this.chipElements.changes.subscribe(() => {
        this.adjustChipOverflow();
      });
    });

    // Prevent closing when clicking outside the settings area to avoid losing in-progress edits
    const backdrop = document.querySelector('.mat-drawer-backdrop');
    if (backdrop) {
      // Suppress the click handler that closes the drawer
      backdrop.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
      }, true);
    }
  }

  /** Pass the settings and count back to the parent screen  */
  onOk(): void {
    const filter: HceSvFilter = this.svFilter;
    const count  = this.countFilter();
    this.dialogRef.close({ filter, count });
  }

  /** Do not pass the settings or count back to the parent screen  */
  onCancel(): void {
    this.dialogRef.close();
  }

  /** Get the total number of filter settings  */
  countFilter(): number {
    // Aggregate card display data (each variable item is already grouped per line)
    return Object.values(this.cardItems)
      .reduce((sum, card) => sum + card.items.length, 0);
  }

  /** Get the number of filter settings */
  getSectionCount(key: SectionKey): number {
    return this.cardItems[key]?.items?.length ?? 0;
  }

  /** Get the total number of filter settings */
  persistEdit(activeSection: SectionKey | null): void {
    this.getFilterFromUI(this.svFilter, activeSection);
    this.closeDrawer();
  }

  /** Close without applying filter settings */
  closeDrawer(): void {
    this.drawerOpened = false;
    this.activeSection = null;
  }

  /** Executed after the closing animation completes */
  onDrawerClosed(): void {
    // Enable interaction only after the closing animation completes to prevent rendering issues when reopening during the animation
    this.drawerClosed = true;
  }

  /** Returns whether the edit button is disabled */
  get isEditButtonDisable(): boolean {
    return !this.drawerClosed;
  }

  /** Get the label of the selected filter */
  get currentSectionLabel(): string {
    if (!this.activeSection) return 'Edit';
    const found = this.sections.find(s => s.key === this.activeSection);
    return found?.label ?? 'Save / Load';
  }

  /** Whether the filter is not selectable */
  isDisabledFilterType(type: string): boolean {
    // Match to Annotationはannotationが選択されている場合のみ選択可能
    return type === 'matchAnnotation' && this.annotations.length <= 0;
  }

  /** Open the settings for the specified filter */
  openSection(filterType: SectionKey): void {
    if (this.isDisabledFilterType(filterType)) {
      return;
    }
    this.activeSection = filterType;

    // Rebuild based on the filter contents before display
    this.applyFilterToUI(this.svFilter, filterType);

    this.drawerOpened = true;
    this.drawerClosed = false;
  }

  /** Whether there are any input errors */
  isInvalid(filterType: string | null): boolean {
    let result = false;
    let ctrlList: NgModel[] = [];
    let formArray: FormArray<FormGroup<any>> | undefined = undefined;
    let formCtrlList: FormControl[] = [];

    // Check target
    switch (filterType) {
      case 'physicalPosition': // Physical Position
        formArray = this.posItems;
        break;
      case 'physicalPositionHide': // Physical Position(Hide)
        formArray = this.posHideItems;
        break;
      case 'svLength': // SV Length
        formArray = this.svLengthItems;
        break;
      case 'svType': // SV Type
        break;
      case 'zygosity': // Zygosity
        break;
      case 'flaggedSV': // Flagged SV
        break;
      case 'geneName': // Gene Name
        formCtrlList.push(this.geneNameForm.get('geneName') as FormControl);
        break;
      case 'svsWithRefGenesDetected': // SVs with Ref Genes detected
        break;
      case 'matchAnnotation': // Match to Annotation
        formArray = this.matchAnnotationItems;
        break;
      case 'minIntervalDepth': // Minimum Interval Depth
        ctrlList.push(this.minIntervalDepthMinCtrl);
        ctrlList.push(this.minIntervalDepthMaxCtrl);
        break;
      case 'medianIntervalDepth': // Median Interval Depth
        ctrlList.push(this.medianIntervalDepthMinCtrl);
        ctrlList.push(this.medianIntervalDepthMaxCtrl);
        break;
      case 'qualityScore': // Quality Score
        ctrlList.push(this.qualityMinCtrl);
        ctrlList.push(this.qualityMaxCtrl);
        break;
      case 'svReason': // SV Reason
        break;
      case 'svBreakpointMoleculeCheck': // SV Breakpoint Molecule Check
        break;
      case 'svsBelowPresenceControlDb': // SVs Below Presence in Control DB (%)
        ctrlList.push(this.svsBelowPresenceCoLoRSdBControlDbCtrl);
        ctrlList.push(this.svsBelowPresenceGnomADControlDbCtrl);
        ctrlList.push(this.svsBelowPresenceOntLRSControlDbCtrl);
        break;
      case 'masked': // Masked
        break;
      case 'bestSVperCluster': // Best SV per Cluster
        break;
    }

    // Perform checks
    if (ctrlList.length > 0) {
      result = result || ctrlList.find(x => x?.invalid ?? false) != undefined;
    }
    if (formArray) {
      result = result || formArray.controls.some(group => group.invalid);
    }
    if (formCtrlList.length > 0) {
      result = result || formCtrlList.find(x => x?.invalid ?? false) != undefined;
    }

    return result;
  }

  // ========= SV Filter edit utilities =========
  
  /**
   * Filter clear.
   * Changes made by clearing items on each settings screen are not applied to this.svFilter until the OK button is pressed.
   */
  onClearFilter(filterType: string | null) {
    let svFilter: HceSvFilter = {};
    switch (filterType) {
      case 'physicalPosition': // Physical Position
        svFilter.physicalPosition = [];
        break;
      case 'physicalPositionHide': // Physical Position(Hide)
        svFilter.physicalPositionHide = [];
        break;
      case 'svLength': // SV Length
        svFilter.svLength = [];
        break;
      case 'svType': // SV Type
        svFilter.svType = [];
        break;
      case 'zygosity': // Zygosity
        svFilter.zygosity = [];
        break;
      case 'flaggedSV': // Flagged SV
        break;
      case 'geneName': // Gene Name
        svFilter.geneName = [];
        break;
      case 'svsWithRefGenesDetected': // SVs with Ref Genes detected
        break;
      case 'matchAnnotation': // Match to Annotation
        svFilter.matchAnnotation = [];
        break;
      case 'minIntervalDepth': // Minimum Interval Depth
        svFilter.minIntervalDepth = {};
        break;
      case 'medianIntervalDepth': // Median Interval Depth
        svFilter.medianIntervalDepth = {};
        break;
      case 'qualityScore': // Quality Score
        svFilter.qualityScore = {};
        break;
      case 'svReason': // SV Reason
        svFilter.svReason = [];
        break;
      case 'svBreakpointMoleculeCheck': // SV Breakpoint Molecule Check
        break;
      case 'svsBelowPresenceControlDb': // SVs Below Presence in Control DB (%)
        svFilter.svsBelowPresenceControlDb = {};
        break;
      case 'masked': // Masked
        break;
      case 'bestSVperCluster': // Best SV per Cluster
        break;
    }

    this.applyFilterToUI(svFilter, filterType);
  }

  /** Clear all filters */
  onClearAllFilter(): void {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '30rem',
      data: {
        title: $localize`Clear Filters?`,
        description: $localize`This will clear all filters.`,
        primaryButtonLabel: $localize`Yes, Clear filters`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true,
        showSecondaryButton: true,
      },
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.svFilter = {};
      this.applyFilterToCardItem(this.svFilter);
    });
  }

  /** Default Filters */
  onDefaultFilter(): void {
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '30rem',
      data: {
        title: $localize`Reset Filters to Default?`,
        description: $localize`This will reset all filters to their default settings.`,
        primaryButtonLabel: $localize`Yes, Reset to Default`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true,
        showSecondaryButton: true,
      },
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.svFilter = this.svFilterSvc.getDefaultFilter();
      this.applyFilterToCardItem(this.svFilter);
    });
  }

  /** Apply filter settings to the screen */
  private applyFilterToUI = (filter: HceSvFilter, filterType: string | null = null): string => {
    let message = '';

    // Physical Position
    if (!filterType || filterType == 'physicalPosition') {
      this.posItems.clear();
      if (filter?.physicalPosition && filter.physicalPosition.length > 0) {
        for (let item of filter.physicalPosition) {
          const chr = item.chr;
          const start = (item.posStart != DEFAULT_POSITION_MIN) ? item.posStart : null;
          const end = (item.posEnd != DEFAULT_POSITION_MAX) ? item.posEnd : null;
          const unit = item.posBp;
          this.addPhysicalPosition('physicalPosition', chr, start, end, unit);
        }
      }
    }

    // Physical Position(Hide)
    if (!filterType || filterType == 'physicalPositionHide') {
      this.posHideItems.clear();
      if (filter?.physicalPositionHide && filter.physicalPositionHide.length > 0) {
        for (let item of filter.physicalPositionHide) {
          const chr = item.chr;
          const start = (item.posStart != DEFAULT_POSITION_MIN) ? item.posStart : null;
          const end = (item.posEnd != DEFAULT_POSITION_MAX) ? item.posEnd : null;
          const unit = item.posBp;
          this.addPhysicalPosition('physicalPositionHide', chr, start, end, unit);
        }
      }
    }

    // SV Length
    if (!filterType || filterType == 'svLength') {
      this.svLengthItems.clear();
      if (filter?.svLength && filter.svLength.length > 0) {
        for (let item of filter.svLength) {
          const lenMin = (item.lenMin != DEFAULT_POSITION_MIN) ? item.lenMin : null;
          const lenMinBp = item.lenMinBp;
          const lenMax = (item.lenMax != DEFAULT_POSITION_MAX) ? item.lenMax : null;
          const lenMaxBp = item.lenMaxBp;
          this.addSvLength(lenMin, lenMinBp, lenMax, lenMaxBp);
        }
      }
    }

    // SV Type
    if (!filterType || filterType == 'svType') {
      this.selectSvTypeByKey = this.buildSelectedFromKeys(this.svTypeKeys, filter.svType);
    }

    // Zygosity
    if (!filterType || filterType == 'zygosity') {
      this.selectZygosityByKey = this.buildSelectedFromKeys(this.zygosityKeys, filter.zygosity);
    }

    // Flagged SV
    if (!filterType || filterType == 'flaggedSV') {
      this.selectFlaggedSvByKey = {'Embedded': filter?.flaggedSV?.includeEmbedded ?? false};
    }

    // Gene Name
    if (!filterType || filterType == 'geneName') {
      this.geneName.setValue(filter?.geneName ? filter?.geneName.join(',') : '');
    }

    // SVs with Ref Genes detected
    if (!filterType || filterType == 'svsWithRefGenesDetected') {
      this.selectOverlapKGByKey = {'OverlapKG': filter?.svsWithRefGenesDetected?.overlapKG ?? false};
    }

    // Match to Annotation
    if (!filterType || filterType == 'matchAnnotation') {
      this.matchAnnotationItems.clear();
      if (filter?.matchAnnotation && filter.matchAnnotation.length > 0) {
        for (let item of filter.matchAnnotation) {
          const annotation = item.annotation;
          const threshold = item.threshold;
          const thresholdunit = item.thresholdunit;
          const excludeMatches = item.excludeMatches;
          this.addMatchAnnotation(annotation, threshold, thresholdunit, excludeMatches);
        }
      }
    }

    // Minimum Interval Depth
    if (!filterType || filterType == 'minIntervalDepth') {
      this.minIntervalDepthMin = filter?.minIntervalDepth?.min != undefined ? String(filter?.minIntervalDepth?.min) : '';
      this.minIntervalDepthMax = filter?.minIntervalDepth?.max != undefined ? String(filter?.minIntervalDepth?.max) : '';
    }

    // Median Interval Depth
    if (!filterType || filterType == 'medianIntervalDepth') {
      this.medianIntervalDepthMin = filter?.medianIntervalDepth?.min != undefined ? String(filter?.medianIntervalDepth?.min) : '';
      this.medianIntervalDepthMax = filter?.medianIntervalDepth?.max != undefined ? String(filter?.medianIntervalDepth?.max) : '';
    }

    // Quality Score
    if (!filterType || filterType == 'qualityScore') {
      this.qualityMin = filter?.qualityScore?.min != undefined ? String(filter?.qualityScore?.min) : '';
      this.qualityMax = filter?.qualityScore?.max != undefined ? String(filter?.qualityScore?.max) : '';
    }

    // SV Reason
    if (!filterType || filterType == 'svReason') {
      this.selectSVReasonByKey = this.buildSelectedFromKeys(this.svReasonKeys, filter.svReason);
    }

    // SV Breakpoint Molecule Check (MolCheck)
    if (!filterType || filterType == 'svBreakpointMoleculeCheck') {
      this.selectMolCheckByKey = {
        'Pass': filter?.svBreakpointMoleculeCheck?.pass ?? false,
        'Fail': filter?.svBreakpointMoleculeCheck?.fail ?? false,
      };
    }

    // SVs Below Presence in Control DB (%)
    if (!filterType || filterType == 'svsBelowPresenceControlDb') {
      this.svsBelowPresenceCoLoRSdBControlDb = filter?.svsBelowPresenceControlDb?.coLoRSdBControlDb != undefined ? String(filter?.svsBelowPresenceControlDb?.coLoRSdBControlDb) : '';
      this.svsBelowPresenceGnomADControlDb = filter?.svsBelowPresenceControlDb?.gnomADControlDb != undefined ? String(filter?.svsBelowPresenceControlDb?.gnomADControlDb) : '';
      this.svsBelowPresenceOntLRSControlDb = filter?.svsBelowPresenceControlDb?.ontLRSControlDb != undefined ? String(filter?.svsBelowPresenceControlDb?.ontLRSControlDb) : '';
    }

    // Masked
    if (!filterType || filterType == 'masked') {
      this.selectMaskedByKey = {
        'ExcludeMasked': filter?.masked?.excludeMasked ?? false,
      };
    }

    // Best SV per Cluster
    if (!filterType || filterType == 'bestSVperCluster') {
      this.selectBestSVperClusterByKey = {
        'ShowBestSVperCluster': filter?.bestSVperCluster?.showBestSVperCluster ?? false,
      };
    }

    return message;
  }

  /** Apply the filter settings to the card display */
  private applyFilterToCardItem (filter: HceSvFilter) {
    // Physical Position
    this.cardItems['physicalPosition'].items = this.makeCardItems(filter, 'physicalPosition');

    // Physical Position(Hide)
    this.cardItems['physicalPositionHide'].items = this.makeCardItems(filter, 'physicalPositionHide');

    // SV Length
    this.cardItems['svLength'].items = this.makeCardItems(filter, 'svLength');

    // SV Type
    this.cardItems['svType'].items = this.makeCardItems(filter, 'svType');

    // Zygosity
    this.cardItems['zygosity'].items = this.makeCardItems(filter, 'zygosity');

    // Flagged SV
    this.cardItems['flaggedSV'].items = this.makeCardItems(filter, 'flaggedSV');

    // Gene Name
    this.cardItems['geneName'].items = this.makeCardItems(filter, 'geneName');

    // SVs with Ref Genes detected
    this.cardItems['svsWithRefGenesDetected'].items = this.makeCardItems(filter, 'svsWithRefGenesDetected');

    // Match to Annotation
    this.cardItems['matchAnnotation'].items = this.makeCardItems(filter, 'matchAnnotation');

    // Minimum Interval Depth
    this.cardItems['minIntervalDepth'].items = this.makeCardItems(filter, 'minIntervalDepth');

    // Median Interval Depth
    this.cardItems['medianIntervalDepth'].items = this.makeCardItems(filter, 'medianIntervalDepth');

    // Quality Score
    this.cardItems['qualityScore'].items = this.makeCardItems(filter, 'qualityScore');

    // SV Reason
    this.cardItems['svReason'].items = this.makeCardItems(filter, 'svReason');

    // SV Breakpoint Molecule Check (MolCheck)
    this.cardItems['svBreakpointMoleculeCheck'].items = this.makeCardItems(filter, 'svBreakpointMoleculeCheck');

    // SVs Below Presence in Control DB (%)
    this.cardItems['svsBelowPresenceControlDb'].items = this.makeCardItems(filter, 'svsBelowPresenceControlDb');

    // Masked
    this.cardItems['masked'].items = this.makeCardItems(filter, 'masked');

    // Best SV per Cluster
    this.cardItems['bestSVperCluster'].items = this.makeCardItems(filter, 'bestSVperCluster');
  }

  /** 
   * Retrieve the UI settings as filter criteria
  */
  private getFilterFromUI(filter: HceSvFilter, section: SectionKey | null = null) {
    // Screen Type
    filter.screenType = SCREEN_TYPE_SV;

    // Physical Position
    if (section == 'physicalPosition') {
      let items: SvFilterPhysicalPosition[] = [];
      for (const group of this.posItems.controls) {
        const chr = group.controls?.chr?.value ?? null;
        const start = group.controls?.posStart?.value?.toString() != "" ? group.controls?.posStart?.value : null;
        const end = group.controls?.posEnd?.value?.toString() != "" ? group.controls?.posEnd?.value : null;
        const unit = group.controls?.posBp?.value ?? null;

        let item: SvFilterPhysicalPosition = {
          chr: chr ?? '',
          posStart: start ?? DEFAULT_POSITION_MIN,
          posEnd: end ?? DEFAULT_POSITION_MAX,
          posBp: Number(unit ?? 1),
        };
        items.push(item);
      }
      filter.physicalPosition = items.length > 0 ? items : undefined;
      this.cardItems['physicalPosition'].items = this.makeCardItems(filter, 'physicalPosition');
    }

    // Physical Position(Hide)
    if (section == 'physicalPositionHide') {
      let items: SvFilterPhysicalPosition[] = [];
      for (const group of this.posHideItems.controls) {
        const chr = group.controls?.chr?.value ?? null;
        const start = group.controls?.posStart?.value?.toString() != "" ? group.controls?.posStart?.value : null;
        const end = group.controls?.posEnd?.value?.toString() != "" ? group.controls?.posEnd?.value : null;
        const unit = group.controls?.posBp?.value ?? null;

        let item: SvFilterPhysicalPosition = {
          chr: chr ?? '',
          posStart: start ?? DEFAULT_POSITION_MIN,
          posEnd: end ?? DEFAULT_POSITION_MAX,
          posBp: Number(unit ?? 1),
        };
        items.push(item);
      }
      filter.physicalPositionHide = items.length > 0 ? items : undefined;
      this.cardItems['physicalPositionHide'].items = this.makeCardItems(filter, 'physicalPositionHide');
    }

    // SV Length
    if (section == 'svLength') {
      let items: SvFilterSvLength[] = [];
      for (const group of this.svLengthItems.controls) {
        const lenMin = group.controls?.lenMin?.value?.toString() != "" ? group.controls?.lenMin?.value : null;
        const lenMinBp = group.controls?.lenMinBp?.value ?? null;
        const lenMax = group.controls?.lenMax?.value?.toString() != "" ? group.controls?.lenMax?.value : null;
        const lenMaxBp = group.controls?.lenMaxBp?.value ?? null;

        let item: SvFilterSvLength = {
          lenMin: lenMin ?? DEFAULT_POSITION_MIN,
          lenMinBp: Number(lenMinBp ?? 1),
          lenMax: lenMax ?? DEFAULT_POSITION_MAX,
          lenMaxBp: Number(lenMaxBp ?? 1),
        };
        items.push(item);
      }
      filter.svLength = items.length > 0 ? items : undefined;
      this.cardItems['svLength'].items = this.makeCardItems(filter, 'svLength');
    }

    // SV Type
    if (section == 'svType') {
      filter.svType = this.getSelectedKeysOrUndefined(this.svTypeKeys, this.selectSvTypeByKey);
      this.cardItems['svType'].items = this.makeCardItems(filter, 'svType');
    }

    // Zygosity
    if (section == 'zygosity') {
      filter.zygosity = this.getSelectedKeysOrUndefined(this.zygosityKeys, this.selectZygosityByKey);
      this.cardItems['zygosity'].items = this.makeCardItems(filter, 'zygosity');
    }

    // Flagged SV
    if (section == 'flaggedSV') {
      filter.flaggedSV = undefined;
      const flaggedSV = this.getSelectedKeysOrUndefined(this.flaggedSvKeys, this.selectFlaggedSvByKey);
      if (flaggedSV) {
        filter.flaggedSV = {
          includeEmbedded: flaggedSV.includes("Embedded"),
        };
      }
      this.cardItems['flaggedSV'].items = this.makeCardItems(filter, 'flaggedSV');
    }

    // Gene Name
    if (section == 'geneName') {
      filter.geneName = undefined;
      const geneName = this.geneNameForm.get('geneName')?.value;
      if (geneName != undefined) {
        const list = this.makeGeneNameValueText(geneName);
        if (list.length > 0) {
          filter.geneName = list;
        }
      }
      this.cardItems['geneName'].items = this.makeCardItems(filter, 'geneName');
    }

    // SVs with Ref Genes detected
    if (section == 'svsWithRefGenesDetected') {
      filter.svsWithRefGenesDetected = undefined;
      const overlapKG = this.getSelectedKeysOrUndefined(this.overlapKGKeys, this.selectOverlapKGByKey);
      if (overlapKG) {
        filter.svsWithRefGenesDetected = {
          overlapKG: overlapKG.includes("OverlapKG"),
        };
      }
      this.cardItems['svsWithRefGenesDetected'].items = this.makeCardItems(filter, 'svsWithRefGenesDetected');
    }

    // Match to Annotation
    if (section == 'matchAnnotation') {
      let items: SvFilterMatchAnnotation[] = [];
      for (const group of this.matchAnnotationItems.controls) {
        const annotation = group.controls?.annotation?.value ?? null;
        const threshold = group.controls?.threshold?.value ?? null;
        const thresholdunit = group.controls?.thresholdunit?.value ?? null;
        const excludeMatches = group.controls?.excludeMatches?.value ?? null;

        let item: SvFilterMatchAnnotation = {
          annotation: annotation ?? '',
          threshold: Number(threshold ?? 0),
          thresholdunit: thresholdunit ?? '',
          excludeMatches: excludeMatches ?? false,
        };
        items.push(item);
      }
      filter.matchAnnotation = items.length > 0 ? items : undefined;
      this.cardItems['matchAnnotation'].items = this.makeCardItems(filter, 'matchAnnotation');
    }

    // Minimum Interval Depth
    if (section == 'minIntervalDepth') {
      filter.minIntervalDepth = undefined;
      if (this.minIntervalDepthMin.length > 0 || this.minIntervalDepthMax.length > 0) {
        filter.minIntervalDepth = {
          min: this.parseNumberOrUndefined(this.minIntervalDepthMin),
          max: this.parseNumberOrUndefined(this.minIntervalDepthMax),
        };
      }
      this.cardItems['minIntervalDepth'].items = this.makeCardItems(filter, 'minIntervalDepth');
    }

    // Median Interval Depth
    if (section == 'medianIntervalDepth') {
      filter.medianIntervalDepth = undefined;
      if (this.medianIntervalDepthMin.length > 0 || this.medianIntervalDepthMax.length > 0) {
        filter.medianIntervalDepth = {
          min: this.parseNumberOrUndefined(this.medianIntervalDepthMin),
          max: this.parseNumberOrUndefined(this.medianIntervalDepthMax),
        };
      }
      this.cardItems['medianIntervalDepth'].items = this.makeCardItems(filter, 'medianIntervalDepth');
    }

    // Quality Score
    if (section == 'qualityScore') {
      filter.qualityScore = undefined;
      if (this.qualityMin.length > 0 || this.qualityMax.length > 0) {
        filter.qualityScore = {
          min: this.parseNumberOrUndefined(this.qualityMin),
          max: this.parseNumberOrUndefined(this.qualityMax),
        };
      }
      this.cardItems['qualityScore'].items = this.makeCardItems(filter, 'qualityScore');
    }

    // SV Reason
    if (section == 'svReason') {
      filter.svReason = undefined;
      const svReason = this.getSelectedKeysOrUndefined(this.svReasonKeys, this.selectSVReasonByKey);
      if (svReason) {
        filter.svReason = svReason;
      }
      this.cardItems['svReason'].items = this.makeCardItems(filter, 'svReason');
    }

    // SV Breakpoint Molecule Check (MolCheck)
    if (section == 'svBreakpointMoleculeCheck') {
      filter.svBreakpointMoleculeCheck = undefined;
      const molCheckt = this.getSelectedKeysOrUndefined(this.molCheckKeys, this.selectMolCheckByKey);
      if (molCheckt) {
        filter.svBreakpointMoleculeCheck = {
          pass: molCheckt.includes("Pass") ?? undefined,
          fail: molCheckt.includes("Fail") ?? undefined,
        }
      }
      this.cardItems['svBreakpointMoleculeCheck'].items = this.makeCardItems(filter, 'svBreakpointMoleculeCheck');
    }

    // SVs Below Presence in Control DB (%)
    if (section == 'svsBelowPresenceControlDb') {
      filter.svsBelowPresenceControlDb = undefined;
      if (
        this.svsBelowPresenceCoLoRSdBControlDb.length > 0 ||
        this.svsBelowPresenceGnomADControlDb.length > 0 ||
        this.svsBelowPresenceOntLRSControlDb.length > 0) {
        filter.svsBelowPresenceControlDb = {
          coLoRSdBControlDb: this.parseNumberOrUndefined(this.svsBelowPresenceCoLoRSdBControlDb),
          gnomADControlDb: this.parseNumberOrUndefined(this.svsBelowPresenceGnomADControlDb),
          ontLRSControlDb: this.parseNumberOrUndefined(this.svsBelowPresenceOntLRSControlDb),
        };
      }
      this.cardItems['svsBelowPresenceControlDb'].items = this.makeCardItems(filter, 'svsBelowPresenceControlDb');
    }

    // Masked
    if (section == 'masked') {
      filter.masked = undefined;
      const masked = this.getSelectedKeysOrUndefined(this.maskedKeys, this.selectMaskedByKey);
      if (masked) {
        filter.masked = {
          excludeMasked: masked.includes('ExcludeMasked') ?? undefined,
        };
      }
      this.cardItems['masked'].items = this.makeCardItems(filter, 'masked');
    }

    // Best SV per Cluster
    if (section == 'bestSVperCluster') {
      filter.bestSVperCluster = undefined;
      const bestSVperCluster = this.getSelectedKeysOrUndefined(this.bestSVperClusterKeys, this.selectBestSVperClusterByKey);
      if (bestSVperCluster) {
        filter.bestSVperCluster = {
          showBestSVperCluster: bestSVperCluster.includes('ShowBestSVperCluster') ?? undefined,
        };
      }
      this.cardItems['bestSVperCluster'].items = this.makeCardItems(filter, 'bestSVperCluster');
    }
  }

  // ========= SV Filter save utilities =========
  
  /** Check whether it can be saved */
  get isDisableSave(): boolean {
    return this.userId.length <= 0 || this.saveName.length <= 0;
  }

  /** On save button press */
  async onSaveSvFilter(saveName: string) {
    /* prompt for confirmation if duplicates exist */
    if (this.saveList.find(x => x.label == saveName)) {
      const dialogRef = this.dialog.open(ModalDialogComponent, {
        width: '50rem',
        data: {
          title: $localize`Override Filter?`,
          description: $localize`You are about to overwrite the filter ‘<strong>${saveName}</strong>’.<br />Do you want to continue?`,
          primaryButtonLabel: $localize`Yes, Override`,
          secondaryButtonLabel: $localize`Cancel`,
          showPrimaryButton: true,
          showSecondaryButton: true,
        },
      });

      const modalInstance = dialogRef.componentInstance;
      modalInstance.primaryAction.subscribe(() => {
        this.saveFilter(saveName);
      });
    } else {
      this.saveFilter(saveName);
    }
  }

  /** Save the filter with the specified name */
  async saveFilter(saveName: string) {
    // Retrieve the filter as strings
    const str = JSON.stringify(this.svFilter);
    const gzip = await this.compressToBase64Builtin(str);

    // save the shorter one
    localStorage.setItem(this.savePrefix + saveName, gzip.length < str.length ? gzip : str);
    this.saveList = this.getSaveList();

    this.snackBar.open(`"${saveName}" has been saved`, undefined, { duration: 1500 });
  }

  /** Load the specified filter */
  async loadFilter(save: SaveItem): Promise<void> {
    let filterString = localStorage.getItem(save.key);
    if (filterString && filterString.length > 0) {
      // If the first character is not '{', treat it as gzip
      if (filterString[0] != '{') {
        filterString = await this.decompressFromBase64Builtin(filterString);
      }
      this.svFilter = JSON.parse(filterString);
      this.removeDisallowedItems(this.svFilter);

      this.applyFilterToUI(this.svFilter);
      this.applyFilterToCardItem(this.svFilter);
      this.saveName = save.key.substring(this.savePrefix.length);

      this.snackBar.open(`"${this.saveName}" has been loaded`, undefined, { duration: 1500 });
    }

    this.closeDrawer();
  }

  /** delete the specified filter */
  deleteSvFilter(save: SaveItem): void {
    const saveName = save.key.substring(this.savePrefix.length);
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      data: {
        title: $localize`Delete Filter?`,
        description: $localize`You are about to delete the filter ‘<strong>${saveName}</strong>’.<br />Deleted filters cannot be restored.`,
        primaryButtonLabel: $localize`Yes, Delete`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true,
        showSecondaryButton: true,
      },
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      localStorage.removeItem(save.key);
      this.snackBar.open(`"${saveName}" has been loaded`, undefined, { duration: 1500 });
      this.saveList = this.getSaveList();
    });
  }

  // ========= Configurable item utilities =========
  
  /** Get the number of parameters being edited for the specified type */
  getEditingParameterCount(type: string | null): number {
    let count = 0;
    switch (type) {
      case 'physicalPosition':
        count = this.posItems.length || 0;
        break;
      case 'physicalPositionHide':
        count = this.posHideItems.length || 0;
        break;
      case 'svLength':
        count = this.svLengthItems.length || 0;
        break;
      case 'matchAnnotation':
        count = this.matchAnnotationItems.length || 0;
        break;
    }

    return count;
  }

  /** is Use Add */
  isUseAdd(type: string | null): boolean {
    let result = false;
    switch (type) {
      case 'physicalPosition':
      case 'physicalPositionHide':
      case 'svLength':
      case 'matchAnnotation':
        result = true;
        break;
    }

    return result;
  }

  /** Register parameters of the specified type with default values */
  addDefaultParameter(type: string | null): void {
    switch (type) {
      case 'physicalPosition':
      case 'physicalPositionHide':
        this.addPhysicalPosition(type);
        break;
      case 'svLength':
        this.addSvLength();
        break;
      case 'matchAnnotation':
        this.addMatchAnnotation();
        break;
    }
  }

  /** add Physical Position / Hide */
  addPhysicalPosition(
    section: SectionKey | null,
    chr: string = '1',
    start: number | null = DEFAULT_POSITION,
    end: number | null = null,
    unit: number = 1
  ): void {
    const group = this.fb.group<PhysicalPositionForm>(
      {
        chr: new FormControl<string | null>(chr, { nonNullable: true }),
        posStart: new FormControl<number | null>(start),
        posEnd: new FormControl<number | null>(end),
        posBp: new FormControl<number | null>(unit, { nonNullable: true }),
      },
      {
        validators: [BothEmptyValidator('posStart', 'posEnd')]
      }
    );

    group.get('posStart')?.valueChanges.subscribe(() => {
      group.updateValueAndValidity({ onlySelf: true });
    });

    group.get('posEnd')?.valueChanges.subscribe(() => {
      group.updateValueAndValidity({ onlySelf: true });
    });

    switch (section) {
      case 'physicalPosition':
        this.posItems.push(group);
        break;
      case 'physicalPositionHide':
        this.posHideItems.push(group);
        break;
    }
  }

  /** add SV Length */
  addSvLength(
    lenMin: number | null = DEFAULT_SV_LENGTH,
    lenMinBp: number | null  = this.unitsKMG[0].value,
    lenMax: number | null = null,
    lenMaxBp: number | null  = this.unitsKMG[0].value,
  ): void {
    const group = this.fb.group<SvLengthForm>(
      {
        lenMin: new FormControl<number | null>(lenMin),
        lenMinBp: new FormControl<number | null>(lenMinBp, { nonNullable: true }),
        lenMax: new FormControl<number | null>(lenMax),
        lenMaxBp: new FormControl<number | null>(lenMaxBp, { nonNullable: true }),
      },
      {
        validators: [BothEmptyValidator('lenMin', 'lenMax')]
      }
    );
    this.svLengthItems.push(group);
  }

  /** add Match to Annotation */
  addMatchAnnotation(
    annotation: string | null = (this.annotations?.[0] ?? ''),
    threshold: number | null = DEFAULT_THRESHOLD,
    thresholdunit: string | null = this.unitsPercent[0].value,
    excludeMatches: boolean | null = null,
  ): void {
    const group = this.fb.group<MatchAnnotationForm>({
      annotation: new FormControl<string | null>(annotation, { nonNullable: true }),
      threshold: new FormControl<number | null>(threshold, {validators: [Validators.required]}),
      thresholdunit: new FormControl<string | null>(thresholdunit, { nonNullable: true }),
      excludeMatches: new FormControl<boolean | null>(excludeMatches),
    });
    this.matchAnnotationItems.push(group);
  }

  /** Delete the specified variable item */
  removePosition(section: SectionKey, index: number): void {
    switch (section) {
      case 'physicalPosition':
        this.posItems.removeAt(index);
        break;
      case 'physicalPositionHide':
        this.posHideItems.removeAt(index);
        break;
      case 'svLength':
        this.svLengthItems.removeAt(index);
        break;
      case 'matchAnnotation':
        this.matchAnnotationItems.removeAt(index);
        break;
    }
  }

  // ========= other Utilities =========

  /** Exclude items that cannot be configured */
  private removeDisallowedItems(filter: HceSvFilter) {
    let message = '';

    if (filter.physicalPosition || filter.physicalPositionHide) {
      // Non-existent chromosomes cannot be configured
      const removed: string[] = [];

      // Physical Position
      if (filter.physicalPosition) {
        const kept: SvFilterPhysicalPosition[] = [];
        for (const m of filter.physicalPosition) {
          if (this.chrs.some(c => c.value === m.chr)) {
            kept.push(m);
          } else {
            removed.push(m.chr);
          }
        }
        filter.physicalPosition = kept;
      }

      // Physical Position(Hide)
      if (filter.physicalPositionHide) {
        const kept: SvFilterPhysicalPosition[] = [];
        for (const m of filter.physicalPositionHide) {
          if (this.chrs.some(c => c.value === m.chr)) {
            kept.push(m);
          } else {
            removed.push(m.chr);
          }
        }
        filter.physicalPositionHide = kept;
      }

      if(removed.length > 0) {
        message += 'The selected chromosome does not exist (' + [...new Set(removed)].join(', ') + ').';
      }
    }

    if (filter.matchAnnotation) {
      const kept: SvFilterMatchAnnotation[] = [];
      const removed: string[] = [];

      // Unselected annotations cannot be configured
      for (const m of filter.matchAnnotation) {
        if (this.annotations.includes(m.annotation)) {
          kept.push(m);
        } else {
          removed.push(m.annotation);
        }
      }
      filter.matchAnnotation = kept;

      if(removed.length > 0) {
        message += 'Some annotations are not selected (' + [...new Set(removed)].join(', ') + ').';
      }
    }

    if (message.length > 0) {
      this.messageModal.openModal(
        $localize`warning`,
        message,
      );
    }
  }

  /** Parse text as a number; return undefined if empty. */
  private parseNumberOrUndefined(value?: string): number | undefined {
    return (value && value?.length > 0) ? Number(value) : undefined;
  }

  /** Generate Gene Name text used for both display and saving */
  private makeGeneNameValueText(text?: string): string[] {
    if (!text) {
      return [];
    }

    const values = text
      .split(/[,\r\n]+/)
      .map(line => line.trim().toUpperCase())
      .filter(line => line.trim().length > 0);

    return values;
  }

  /** Key name array (compatible with const objects). */
  private getKeys<TObj extends Record<string, string>>(obj: TObj) {
    return Object.keys(obj) as Array<keyof TObj>;
  }

  /** Initialize Record<key name, boolean> with false values */
  private buildInitialSelectedByKey<TObj extends Record<string, string>>(
    obj: TObj
  ): Record<keyof TObj, boolean> {
    return this.getKeys(obj).reduce((acc, k) => {
      acc[k] = false;
      return acc;
    }, {} as Record<keyof TObj, boolean>);
  }

  /** Get the array of selected key names (filter settings) */
  private getSelectedKeysOrUndefined<TKey extends string>(
    keys: readonly TKey[],
    selected: Record<TKey, boolean>
  ): TKey[] | undefined {
    const list = keys.filter(k => selected[k])
    return list.length > 0 ? list : undefined;
  }

  /**
   * Create a new Record setting true only for keys listed in selectedKeys
   */
  private buildSelectedFromKeys<TKey extends string>(
    keys: readonly TKey[],
    selectedKeys?: readonly TKey[]
  ): Record<TKey, boolean> {
    const selectedSet = new Set(selectedKeys ?? []);
    return keys.reduce((acc, k) => {
      acc[k] = selectedSet.has(k);
      return acc;
    }, {} as Record<TKey, boolean>);
  }
  
  /**
   * Retrieve a list of keys within the namespace that start with savePrefix
   */
  private getSaveList(): SaveItem[] {
    const list: SaveItem[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      if (key.startsWith(this.savePrefix)) {
        const label = key.substring(this.savePrefix.length);
        list.push({ key, label });
      }
    }

    // Sort by label in ascending order (natural order, case-insensitive)
    list.sort((a, b) =>
      a.label.localeCompare(b.label, 'ja', { numeric: true, sensitivity: 'base' })
    );
    return list;
  }

  // ========= summary cards grid utilities =========

  /** Chip expand/collapse within the card */
  toggleExpand(key: string): void {
    this.cardExpanded[key] = !this.cardExpanded[key];
  }

  /** Wait until chipElements are ready */
  private waitForChipElements(attempts: number = 10) {
    if (this.chipElements && this.chipElements.length > 0) {
      this.adjustChipOverflow();
      return;
    }

    if (attempts <= 0) {
      return;
    }

    setTimeout(() => this.waitForChipElements(attempts - 1), 30);
  }

  /** Adjust to prevent overflowing the card chips */
  private adjustChipOverflow() {
    this.chipElements.forEach((chipRef) => {
      const el = chipRef.nativeElement as HTMLElement;
      if (!el) {
        return;
      }

      const subEl = el.querySelector('.chip-sub') as HTMLElement | null;
      const mainEl = el.querySelector('.chip-main') as HTMLElement | null;
      if (!subEl || !mainEl) {
        return;
      }

      // Base it on the width of the chip’s parent container (this prevents the left side from becoming abnormally short)
      const chipSetWidth = el.parentElement?.clientWidth ?? el.clientWidth;
      const mainWidth = mainEl.scrollWidth;
      const padding = 21; // Adjust the right margin

      // Determine the maximum width of the sub element based on the chip’s original allowable maximum width
      const margin = 5; // Margin between main and sub
      const subMaxWidth = Math.max(0, chipSetWidth - mainWidth - padding - margin);

      // Apply the maximum width to the sub element
      subEl.style.maxWidth = `${subMaxWidth}px`;
      if (mainWidth > 0) {
        subEl.style.marginRight = `${margin}px`;
      }
    });
  }

  /** make summary card items */
  private makeCardItems(filter: HceSvFilter, section: SectionKey | null = null): CardChip[] {
    let cardChips: CardChip[] = [];
    switch (section) {
      case 'physicalPosition': // Physical Position
        {
          let chips: CardChip[] = [];
          if (filter?.physicalPosition && filter.physicalPosition.length > 0) {
            for (let item of filter.physicalPosition) {
              const chr = item.chr;
              const start = (item.posStart != DEFAULT_POSITION_MIN) ? item.posStart : null;
              const end = (item.posEnd != DEFAULT_POSITION_MAX) ? item.posEnd : null;
              const unit = item.posBp;
              // Group into a single item
              let lineitems: string[] = [];
              this.pushCardLabelText(lineitems, chr, 'chr', this.chrs);
              this.pushCardLabelText(lineitems, start, 'Start:');
              if (start != null) {
                this.pushCardLabelText(lineitems, unit, '', this.unitsKMG)
              };
              this.pushCardLabelText(lineitems, end, 'End:');
              if (end != null) {
                this.pushCardLabelText(lineitems, unit, '', this.unitsKMG);
              }
              chips.push({main :lineitems.join(' ')});
            }
          }
          cardChips = chips;
        }
        break;
      case 'physicalPositionHide': // Physical Position (Hide)
        {
          let chips: CardChip[] = [];
          if (filter?.physicalPositionHide && filter.physicalPositionHide.length > 0) {
            for (let item of filter.physicalPositionHide) {
              const chr = item.chr;
              const start = (item.posStart != DEFAULT_POSITION_MIN) ? item.posStart : null;
              const end = (item.posEnd != DEFAULT_POSITION_MAX) ? item.posEnd : null;
              const unit = item.posBp;
              // Group into a single item
              let lineitems: string[] = [];
              this.pushCardLabelText(lineitems, chr, 'chr', this.chrs);
              this.pushCardLabelText(lineitems, start, 'Start:');
              if (start != null) {
                this.pushCardLabelText(lineitems, unit, '', this.unitsKMG)
              };
              this.pushCardLabelText(lineitems, end, 'End:');
              if (end != null) {
                this.pushCardLabelText(lineitems, unit, '', this.unitsKMG);
              }
              chips.push({main :lineitems.join(' ')});
            }
          }
          cardChips = chips;
        }
        break;
      case 'svLength': // SV Length
        {
          let chips: CardChip[] = [];
          if (filter?.svLength && filter.svLength.length > 0) {
            for (let item of filter.svLength) {
              const lenMin = (item.lenMin != DEFAULT_POSITION_MIN) ? item.lenMin : null;
              const lenMinBp = item.lenMinBp;
              const lenMax = (item.lenMax != DEFAULT_POSITION_MAX) ? item.lenMax : null;
              const lenMaxBp = item.lenMaxBp;
              // Group into a single item
              let lineitems: string[] = [];
              if (lenMin != null) {
                this.pushCardLabelText(lineitems, lenMin, 'Min:');
                this.pushCardLabelText(lineitems, lenMinBp, '', this.unitsKM);
              }
              if (lenMax != null) {
                this.pushCardLabelText(lineitems, lenMax, 'Max:');
                this.pushCardLabelText(lineitems, lenMaxBp, '', this.unitsKM);
              }
              chips.push({main :lineitems.join(' ')});
            }
          }
          cardChips = chips;
        }
        break;
      case 'svType': // SV Type
        cardChips = filter.svType ? filter.svType.map(main => ({ main })) : [];
        break;
      case 'zygosity': // Zygosity
        cardChips = filter.zygosity ? filter.zygosity.map(main => ({ main })) : [];
        break;
      case 'flaggedSV': // Flagged SV
        cardChips = filter?.flaggedSV?.includeEmbedded ? [{main: 'Include Embedded'}] : [];
        break;
      case 'geneName': // Gene Name
        // Because the text may be long, set it as a sub element that supports truncation
        cardChips = filter?.geneName ? filter.geneName.map(sub => ({ sub, main: '' })) : [];
        break;
      case 'svsWithRefGenesDetected': // SVs with Ref Genes detected
        cardChips = filter?.svsWithRefGenesDetected?.overlapKG ? [{main: 'OverlapKG'}]: [];
        break;
      case 'matchAnnotation': // Match to Annotation
        {
          let chips: CardChip[] = [];
          if (filter?.matchAnnotation && filter.matchAnnotation.length > 0) {
            for (let item of filter.matchAnnotation) {
              const annotation = item.annotation;
              const threshold = item.threshold;
              const thresholdunit = item.thresholdunit;
              const excludeMatches = item.excludeMatches;

              // Group into a single item
              let lineitems: string[] = [];
              this.pushCardLabelText(lineitems, threshold);
              this.pushCardLabelText(lineitems, thresholdunit, '', this.unitsPercent);
              if (excludeMatches) {
                lineitems.push('Exclude Matches');
              }
              chips.push({
                sub: annotation,
                main :lineitems.join(' ')
              });
            }
          }
          cardChips = chips;
        }
        break;
      case 'minIntervalDepth': // Minimum Interval Depth
        cardChips = this.makeInputValueText([
          {name: 'Min', value: filter?.minIntervalDepth?.min ?? ''},
          {name: 'Max', value: filter?.minIntervalDepth?.max ?? ''},
        ]);
        break;
      case 'medianIntervalDepth': // Median Interval Depth
        cardChips = this.makeInputValueText([
          {name: 'Min', value: filter?.medianIntervalDepth?.min ?? ''},
          {name: 'Max', value: filter?.medianIntervalDepth?.max ?? ''},
        ]);
        break;
      case 'qualityScore': // Quality Score
        cardChips = this.makeInputValueText([
          {name: 'Min', value: filter?.qualityScore?.min ?? ''},
          {name: 'Max', value: filter?.qualityScore?.max ?? ''},
        ]);
        break;
      case 'svReason': // SV Reason
        cardChips = filter?.svReason ? filter?.svReason.map(main => ({ main })) : [];
        break;
      case 'svBreakpointMoleculeCheck': // SV Breakpoint Molecule Check (MolCheck)
        if (filter?.svBreakpointMoleculeCheck?.pass) {
          cardChips.push({main: 'Pass'});
        }
        if (filter?.svBreakpointMoleculeCheck?.fail) {
          cardChips.push({main: 'Fail'});
        }
        break;
      case 'svsBelowPresenceControlDb': // SVs Below Presence in Control DB (%)
        cardChips = this.makeInputValueText([
          {name: 'CoLoRSdb', value: filter?.svsBelowPresenceControlDb?.coLoRSdBControlDb ?? ''},
          {name: 'gnomAD', value: filter?.svsBelowPresenceControlDb?.gnomADControlDb ?? ''},
          {name: 'ONT LRS', value: filter?.svsBelowPresenceControlDb?.ontLRSControlDb ?? ''},
        ]);
        break;
      case 'masked': // Masked
        cardChips = filter?.masked ? [{main: 'Exclude Masked'}] : [];
        break;
      case 'bestSVperCluster': // Best SV per Cluster
        cardChips = filter?.bestSVperCluster ? [{main: 'Show best SV per cluster'}] : [];
        break;
    }
    return cardChips;
  }

  /**
   * Add the specified content to the array of card labels
   * @param items the array to add to
   * @param value set value
   * @param prefix set prefix
   * @param labels Specify when using the display value of the selection list
   */
  private pushCardLabelText(items: string[], value: number | string | null, prefix: string = '', labels: Array<{ value: number | string; label: string }> | null = null) {
    if (value != null) {
      if (labels) {
        items.push(prefix + (labels.find(x => x.value === value)?.label ?? value));
      } else {
        items.push(prefix + value.toString());
      }
    }
  }

  /** Create display text */
  private makeInputValueText(params: Array<NameValue>): CardChip[] {
    let items:CardChip[] = [];
    for (const data of params) {
      const str = data?.value?.toString();
      if (str.length > 0 ) {
        items.push({main: data.name + ': ' + str});
      }
    }
    return items;
  }

  // ========= gzip Utilities =========

  /**
   * Compress a string using gzip and return it as a Base64 string
   * @param input  The string to be compressed
   */
  async compressToBase64Builtin(
    input: string
  ): Promise<string> {
    if (typeof (globalThis as any).CompressionStream === 'undefined') {
      console.warn('This environment does not support CompressionStream');
      return input;
    }

    const encoder = new TextEncoder();
    const inputBytes = encoder.encode(input);

    // Convert a byte array to a ReadableStream
    const readable = new ReadableStream<Uint8Array>({
      start(controller) {
        controller.enqueue(inputBytes);
        controller.close();
      },
    });

    const compressedStream = readable.pipeThrough(
      new CompressionStream('gzip' as CompressionFormat)
    );

    const compressedBytes = new Uint8Array(await new Response(compressedStream).arrayBuffer());
    return this.uint8ToBase64(compressedBytes);
  }

  /**
   * Decompress and restore a gzip-compressed Base64 string
   */
  async decompressFromBase64Builtin(
    base64: string
  ): Promise<string> {
    if (typeof (globalThis as any).DecompressionStream === 'undefined') {
      throw new Error('DecompressionStream 未対応の環境です');
    }

    const compressedBytes = this.base64ToUint8(base64);

    const readable = new ReadableStream<Uint8Array>({
      start(controller) {
        controller.enqueue(compressedBytes);
        controller.close();
      },
    });

    const decompressedStream = readable.pipeThrough(
      new DecompressionStream('gzip' as CompressionFormat)
    );

    const decompressedBytes = new Uint8Array(await new Response(decompressedStream).arrayBuffer());
    return new TextDecoder().decode(decompressedBytes);
  }

  /** Uint8Array to Base64（Safe chunk processing even for large arrays） */
  private uint8ToBase64(bytes: Uint8Array): string {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
  }

  /** Base64 to Uint8Array */
  private base64ToUint8(base64: string): Uint8Array {
    const binary = atob(base64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }

  private getUserId(): string | null {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        return null;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.user_id || null;
    } catch (e) {
      return null;
    }
  }
}
